import React, {Component} from "react";
import {Box, Drawer, IconButton, Tooltip, Typography} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import AiChatApp from "../sidebarTop_apps/aiChatApp";
import {openAiChatPopout} from "../utils/slide_ai_popout";

const HEAD_BAR_HEIGHT = 48;
const DEFAULT_WIDTH = 420;
const MIN_WIDTH = 320;
const MAX_WIDTH_RATIO = 0.85;
const WIDTH_STORAGE_KEY = "morphle.slideAi.popoutWidth";

const clampWidth = (w) => {
	const max = Math.max(MIN_WIDTH, Math.floor(window.innerWidth * MAX_WIDTH_RATIO));
	return Math.min(max, Math.max(MIN_WIDTH, Math.round(w)));
};

const readStoredWidth = () => {
	try {
		const n = Number(localStorage.getItem(WIDTH_STORAGE_KEY));
		if (Number.isFinite(n) && n >= MIN_WIDTH) return clampWidth(n);
	} catch (e) { /* ignore */ }
	return DEFAULT_WIDTH;
};

/**
 * Full-height right-side Morpho Chat panel.
 * Sidebar icon toggles this panel only (no left-drawer chat).
 * Left edge is drag-resizable; width persists in localStorage.
 */
class AiChatPopoutOverlay extends Component {
	constructor(props) {
		super(props);
		this.state = {
			open: false,
			width: readStoredWidth(),
			resizing: false,
		};
		this.resizeStartX = 0;
		this.resizeStartWidth = DEFAULT_WIDTH;
	}

	componentDidMount() {
		this.onOpen = () => this.setState({open: true, width: clampWidth(this.state.width)});
		this.onClose = () => this.setState({open: false, resizing: false});
		this.onToggle = () => this.setState((prev) => ({
			open: !prev.open,
			width: clampWidth(prev.width),
			resizing: false,
		}));
		window.addEventListener("openAiChatPopout", this.onOpen);
		window.addEventListener("closeAiChatPopout", this.onClose);
		window.addEventListener("toggleAiChatPopout", this.onToggle);
		window.addEventListener("resize", this.onWindowResize);
		// Deep-link from Morpho Chat history / shared URLs: ?morphoChat=1
		try {
			const params = new URLSearchParams(window.location.search || "");
			if (params.get("morphoChat") === "1" || params.get("morpho_chat") === "1") {
				setTimeout(() => openAiChatPopout(), 400);
			}
		} catch (e) { /* ignore */ }
	}

	componentDidUpdate(prevProps, prevState) {
		if (prevState.open !== this.state.open || prevState.width !== this.state.width) {
			this.syncBodyChrome();
		}
	}

	/** Viewer chrome (zoom stack, scale bar) flips to the left while the chat owns the right edge. */
	syncBodyChrome = (forceClosed = false) => {
		const body = document.body;
		if (!body) return;
		const open = this.state.open && !forceClosed;
		body.classList.toggle("morpho-chat-open", open);
		if (open) body.style.setProperty("--morpho-chat-width", `${this.state.width}px`);
		else body.style.removeProperty("--morpho-chat-width");
	};

	componentWillUnmount() {
		this.syncBodyChrome(true);
		window.removeEventListener("openAiChatPopout", this.onOpen);
		window.removeEventListener("closeAiChatPopout", this.onClose);
		window.removeEventListener("toggleAiChatPopout", this.onToggle);
		window.removeEventListener("resize", this.onWindowResize);
		this.stopResizeListeners();
	}

	onWindowResize = () => {
		this.setState((prev) => {
			const next = clampWidth(prev.width);
			return next === prev.width ? null : {width: next};
		});
	};

	persistWidth = (width) => {
		try {
			localStorage.setItem(WIDTH_STORAGE_KEY, String(width));
		} catch (e) { /* ignore */ }
	};

	stopResizeListeners = () => {
		window.removeEventListener("mousemove", this.onResizeMove);
		window.removeEventListener("mouseup", this.onResizeEnd);
	};

	onResizeStart = (e) => {
		e.preventDefault();
		e.stopPropagation();
		this.resizeStartX = e.clientX;
		this.resizeStartWidth = this.state.width;
		this.setState({resizing: true});
		window.addEventListener("mousemove", this.onResizeMove);
		window.addEventListener("mouseup", this.onResizeEnd);
	};

	onResizeMove = (e) => {
		// Dragging the left edge: move left → wider, move right → narrower.
		const delta = this.resizeStartX - e.clientX;
		const next = clampWidth(this.resizeStartWidth + delta);
		this.setState({width: next});
	};

	onResizeEnd = () => {
		this.stopResizeListeners();
		this.setState((prev) => {
			this.persistWidth(prev.width);
			return {resizing: false};
		});
	};

	handleClose = () => {
		this.setState({open: false, resizing: false});
	};

	render() {
		const {open, width, resizing} = this.state;
		return (
			<Drawer
				anchor="right"
				variant="persistent"
				open={open}
				onClose={this.handleClose}
				hideBackdrop
				PaperProps={{
					elevation: 8,
					style: {
						backgroundColor: "rgba(20, 20, 28, 0.96)",
						color: "#fff",
						width,
						maxWidth: "100vw",
						top: `${HEAD_BAR_HEIGHT}px`,
						height: `calc(100% - ${HEAD_BAR_HEIGHT}px)`,
						zIndex: 1300,
						backdropFilter: "blur(10px)",
						borderLeft: "1px solid rgba(255,255,255,0.12)",
						transition: resizing ? "none" : undefined,
						overflow: "visible",
						boxSizing: "border-box",
					},
				}}
				sx={{
					flexShrink: 0,
					"& .MuiDrawer-paper": {
						overflow: "visible",
					},
				}}
			>
				{/* Left-edge resize handle */}
				<Box
					onMouseDown={this.onResizeStart}
					sx={{
						position: "absolute",
						left: 0,
						top: 0,
						bottom: 0,
						width: 8,
						ml: "-4px",
						cursor: "col-resize",
						zIndex: 2,
						touchAction: "none",
						"&:hover, &:active": {
							backgroundColor: "rgba(255,255,255,0.12)",
						},
						...(resizing ? {backgroundColor: "rgba(158,203,255,0.35)"} : {}),
					}}
					title="Drag to resize"
				/>
				<Box
					sx={{
						display: "flex",
						flexDirection: "column",
						height: "100%",
						minHeight: 0,
						p: 1.5,
						boxSizing: "border-box",
						userSelect: resizing ? "none" : "auto",
					}}
				>
					<Box
						sx={{
							display: "flex",
							alignItems: "center",
							justifyContent: "space-between",
							mb: 1,
							pb: 0.75,
							borderBottom: "1px solid rgba(255,255,255,0.12)",
							flexShrink: 0,
						}}
					>
						<Typography sx={{color: "#fff", fontWeight: 600, fontSize: "0.95rem", letterSpacing: 0.2}}>
							Morpho Chat
						</Typography>
						<Tooltip title="Close">
							<IconButton size="small" onClick={this.handleClose} sx={{color: "rgba(255,255,255,0.85)"}}>
								<CloseIcon fontSize="small" />
							</IconButton>
						</Tooltip>
					</Box>
					<Box sx={{flex: 1, minHeight: 0, display: "flex", flexDirection: "column"}}>
						{open ? (
							<AiChatApp variant="popout" hideTitle />
						) : null}
					</Box>
				</Box>
			</Drawer>
		);
	}
}

export default AiChatPopoutOverlay;
